import { useRef, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Film, X, GripVertical } from 'lucide-react';
import { Modal } from '../../ComponnentsUI/common/Modal';
import React from 'react';
import sweep01 from '../../assets/sweeps/sweep1.gif';
import sweep02 from '../../assets/sweeps/sweep2.gif';
import sweep03 from '../../assets/sweeps/sweep3.gif';
import sweep04 from '../../assets/sweeps/sweep4.gif';
import sweep05 from '../../assets/sweeps/sweep5.gif';
import sweep06 from '../../assets/sweeps/sweep6.gif';
import sweep07 from '../../assets/sweeps/sweep7.gif';
import scansImage from '../../assets/scans_00000.png';

interface InstanceData {
  id: number;
  sopInstanceUID: string;
  sopClassUID: string;
  seriesInstanceUID: string;
  instanceNumber: string;
  transferSyntax: string;
  pixelSpacing: string;
  numberOfFrames: number;
  receivedAt: string;
  synced: boolean;
  syncAttemptedAt: string | null;
  syncError: string | null;
  filePath?: string;
  signedUrl?: string;
  signedUrlExpiry?: string;
}

interface VideoPreviewModalProps {
  isOpen: boolean;
  instances: InstanceData[];
  currentIndex: number;
  selectedInstances: Set<number>;
  onClose: () => void;
  onNavigate: (direction: 'prev' | 'next') => void;
  onToggleSelection: (instanceId: number) => void;
  examType?: string;
  blindOrder?: number[];
  onBlindOrderChange?: (order: number[]) => void;
  selectedSweepBox?: number | null;
  onAssignToSweep?: (instanceId: number, sweepIndex: number) => void;
  onSelectSweepBox?: (sweepIndex: number) => void;
  useIndexMode?: boolean;
}

export const VideoPreviewModal: React.FC<VideoPreviewModalProps> = ({
  isOpen,
  instances,
  currentIndex,
  selectedInstances,
  onClose,
  onNavigate,
  onToggleSelection,
  examType,
  blindOrder = [],
  onBlindOrderChange,
  selectedSweepBox,
  onAssignToSweep,
  onSelectSweepBox,
  useIndexMode = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [draggedItem, setDraggedItem] = useState<number | null>(null);
  const [dragOverItem, setDragOverItem] = useState<number | null>(null);

  const sweepMovements = {
    0: { gif: sweep01, name: 'Vertical Center' },
    1: { gif: sweep02, name: 'Vertical Left' },
    2: { gif: sweep03, name: 'Vertical Up' },
    3: { gif: sweep04, name: 'Horizontal R→L' },
    4: { gif: sweep05, name: 'Horizontal L→R' },
    5: { gif: sweep06, name: 'Curved R→L' },
    6: { gif: sweep07, name: 'Curved L→R' },
  };

  // Sweep animation paths and configuration (same as InstanceSelector)
  const sweepPaths = {
    'ultrasound-sweep-1': 'M 50 90 V 65 M 85 65 H 15',
    'ultrasound-sweep-2': 'M 25 35 V 85',
    'ultrasound-sweep-3': 'M 50 35 V 85',
    'ultrasound-sweep-4': 'M 75 35 V 85',
    'ultrasound-sweep-5': 'M 15 50 H 85',
    'ultrasound-sweep-6': 'M 15 75 H 85',
    'ultrasound-sweep-7': 'M 20 80 Q 50 100 80 80',
  };

  const getAnimationDetails = (animationClass: string) => {
    if (animationClass.includes('sweep-1'))
      return { duration: '3s', timing: 'ease-in-out', alternate: true };
    if (
      animationClass.includes('sweep-2') ||
      animationClass.includes('sweep-3') ||
      animationClass.includes('sweep-4')
    )
      return { duration: '2.5s', timing: 'ease-in-out', alternate: true };
    if (
      animationClass.includes('sweep-5') ||
      animationClass.includes('sweep-6')
    )
      return { duration: '3s', timing: 'ease-in-out', alternate: true };
    if (animationClass.includes('sweep-7'))
      return { duration: '3.5s', timing: 'ease-in-out', alternate: true };
    return { duration: '3s', timing: 'ease-in-out', alternate: true };
  };

  const sweepAnimationClasses = [
    'ultrasound-sweep-1',
    'ultrasound-sweep-2',
    'ultrasound-sweep-3',
    'ultrasound-sweep-4',
    'ultrasound-sweep-5',
    'ultrasound-sweep-6',
    'ultrasound-sweep-7',
  ];

  // SweepAnimation Component (same as InstanceSelector)
  const SweepAnimation = ({ animationClass }: { animationClass: string }) => {
    const pathRef = useRef<SVGPathElement>(null);
    const [pathLength, setPathLength] = useState(0);

    const pathD = sweepPaths[animationClass as keyof typeof sweepPaths] || '';
    const { duration, timing, alternate } = getAnimationDetails(animationClass);
    const dashPattern = '6 6';

    useEffect(() => {
      if (pathRef.current && pathD) {
        setPathLength(pathRef.current.getTotalLength());
      }
    }, [pathD]);

    const pathAnimationName = 'draw-path';

    return (
      <div
        className="h-[60px] w-[60px] rounded-md overflow-hidden relative bg-no-repeat bg-center"
        style={{ backgroundImage: `url(${scansImage})`, backgroundSize: '80%' }}
      >
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <path
            ref={pathRef}
            d={pathD}
            fill="none"
            // stroke="rgba(60, 160, 255, 0.7)"
            // strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray={dashPattern}
            style={{
              '--path-length': pathLength,
              strokeDashoffset: pathLength,
              animation:
                pathLength > 0
                  ? `${pathAnimationName} ${duration} ${timing} infinite ${alternate ? 'alternate' : ''}`
                  : 'none',
            } as React.CSSProperties}
          />
        </svg>
        <div
          className={`absolute w-1.5 h-3 bg-blue-500 rounded-sm shadow-md ${animationClass}`}
        />
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  useEffect(() => {
    if (isOpen && videoRef.current && instances[currentIndex]?.signedUrl) {
      const currentInstance = instances[currentIndex];
      videoRef.current.src = currentInstance.signedUrl;
      videoRef.current.load();
      videoRef.current.play().catch(() => {
        // Handle autoplay failure silently
      });
    }
  }, [isOpen, currentIndex, instances]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!isOpen) return;
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          onNavigate('prev');
          break;
        case 'ArrowRight':
          e.preventDefault();
          onNavigate('next');
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        case ' ':
          e.preventDefault();
          if (instances[currentIndex] && !instances[currentIndex].synced) {
            onToggleSelection(instances[currentIndex].id);
          }
          break;
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, currentIndex, instances, onNavigate, onClose, onToggleSelection]);


  if (!instances.length) {
    return null;
  }

  const currentInstance = instances[currentIndex];
  const isBlindStudy = examType === 'Blind';

  const getInstanceSweepNumber = (instanceId: number) => {
    const index = blindOrder.indexOf(instanceId);
    return index !== -1 ? index + 1 : null;
  };

  const handleDragStart = (e: React.DragEvent, instanceId: number) => {
    if (!isBlindStudy || !selectedInstances.has(instanceId)) return;
    setDraggedItem(instanceId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, instanceId: number) => {
    if (!isBlindStudy || !selectedInstances.has(instanceId) || draggedItem === instanceId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverItem(instanceId);
  };

  const handleDrop = (e: React.DragEvent, targetInstanceId: number) => {
    e.preventDefault();
    if (!isBlindStudy || !draggedItem || draggedItem === targetInstanceId) return;

    const currentOrder = [...blindOrder];
    const draggedIndex = currentOrder.indexOf(draggedItem);
    const targetIndex = currentOrder.indexOf(targetInstanceId);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      currentOrder.splice(draggedIndex, 1);
      const newTargetIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
      currentOrder.splice(newTargetIndex, 0, draggedItem);
      onBlindOrderChange?.(currentOrder);
    }

    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleInstanceSelection = () => {
    if (!currentInstance || currentInstance.synced) return;

    if (isBlindStudy) {
      const isCurrentlySelected = selectedInstances.has(currentInstance.id);
      
      if (isCurrentlySelected) {
        const currentOrder = [...(blindOrder || [])];
        const currentIndex = currentOrder.indexOf(currentInstance.id);
        
        if (currentIndex !== -1) {
          currentOrder[currentIndex] = 0;
          onBlindOrderChange?.(currentOrder);
        }
        
        onToggleSelection(currentInstance.id);
        
        if (onSelectSweepBox) {
          onSelectSweepBox(null);
        }
      } else {
        if (selectedSweepBox !== null && selectedSweepBox !== undefined) {
          if (onAssignToSweep) {
            onAssignToSweep(currentInstance.id, selectedSweepBox);
          } else {
            const currentOrder = [...(blindOrder || [])];
            
            const previousInstanceId = currentOrder[selectedSweepBox];
            
            const currentIndex = currentOrder.indexOf(currentInstance.id);
            if (currentIndex !== -1) {
              currentOrder[currentIndex] = 0;
            }
            
            currentOrder[selectedSweepBox] = currentInstance.id;
            
            if (previousInstanceId !== 0 && previousInstanceId !== currentInstance.id) {
              onToggleSelection(previousInstanceId);
            }
            
            onBlindOrderChange?.(currentOrder);
            onToggleSelection(currentInstance.id);
          }
          
          if (onSelectSweepBox) {
            onSelectSweepBox(null);
          }
        } else {
          alert('Please select a sweep box first before assigning an instance.');
        }
      }
    } else {
      onToggleSelection(currentInstance.id);
    }
  };

  const handleSweepBoxClick = (sweepIndex: number) => {
    if (onSelectSweepBox) {
      onSelectSweepBox(sweepIndex === selectedSweepBox ? null : sweepIndex);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center gap-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Instance #{currentInstance?.instanceNumber}</h3>
          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            <span>{currentInstance?.numberOfFrames} frames</span>
            <span>{currentInstance && formatDate(currentInstance.receivedAt)}</span>
            {isBlindStudy && selectedInstances.has(currentInstance?.id) && (
              <div className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400">
                {useIndexMode
                  ? `Sweep ${getInstanceSweepNumber(currentInstance.id)}`
                  : sweepMovements[(getInstanceSweepNumber(currentInstance.id)! - 1) as keyof typeof sweepMovements]
                      ?.name}
              </div>
            )}
            {currentInstance?.synced ? (
              <div className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                Already Synced
              </div>
            ) : (
              <div
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  selectedInstances.has(currentInstance?.id)
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                }`}
              >
                {selectedInstances.has(currentInstance?.id)
                  ? isBlindStudy
                    ? 'Assigned'
                    : 'Selected'
                  : isBlindStudy
                    ? 'Not Assigned'
                    : 'Not Selected'}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {currentIndex + 1} of {instances.length}
          </span>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      <Modal.Body className="p-0 relative h-[605px] bg-black">
        {currentInstance?.signedUrl ? (
          <video ref={videoRef} controls autoPlay loop className="w-full h-full object-contain" crossOrigin="anonymous">
            <source src={currentInstance.signedUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-gray-800">
            <div className="text-center">
              <Film className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No video available for this instance</p>
            </div>
          </div>
        )}


        <button
          onClick={() => onNavigate('prev')}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-all"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={() => onNavigate('next')}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-all"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </Modal.Body>

      {isBlindStudy && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Sweep Assignment Overview</h4>
          </div>
          <div className="grid grid-cols-7 gap-2 mb-4">
            {Array.from({ length: 7 }, (_, index) => {
              const sweepNumber = index + 1;
              const assignedInstanceId = blindOrder[index] || 0;
              const assignedInstance =
                assignedInstanceId !== 0 ? instances.find((i) => i.id === assignedInstanceId) : null;
              const isCurrentInstance = currentInstance?.id === assignedInstanceId;
              const isSelectedSweepBox = selectedSweepBox === index;

              return (
                <div
                  key={sweepNumber}
                  onClick={() => handleSweepBoxClick(index)}
                  className={`p-2 border rounded-lg text-center transition-all cursor-pointer hover:border-purple-400 ${
                    isSelectedSweepBox
                      ? 'border-purple-500 bg-purple-100 dark:bg-purple-900/30 ring-2 ring-purple-300 dark:ring-purple-600'
                      : isCurrentInstance
                        ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30 ring-2 ring-blue-200 dark:ring-blue-600'
                        : assignedInstance
                          ? 'border-purple-300 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-600'
                          : 'border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700'
                  }`}
                >
                  <div className="text-xs font-medium text-purple-700 dark:text-purple-400">
                    {useIndexMode ? `Sweep ${sweepNumber}` : sweepMovements[index as keyof typeof sweepMovements]?.name}
                    {isSelectedSweepBox && <span className="ml-1 text-purple-600 dark:text-purple-400">●</span>}
                  </div>
                  {assignedInstance ? (
                    <div>
                      <div className="text-xs font-medium text-gray-900 dark:text-gray-100">#{assignedInstance.instanceNumber}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">{assignedInstance.numberOfFrames}f</div>
                    </div>
                  ) : (
                    // Conditionally render animation based on useIndexMode
                    <div className="w-full h-full flex items-center justify-center">
                      {!useIndexMode ? (
                        <SweepAnimation
                          animationClass={
                            sweepAnimationClasses[
                              index % sweepAnimationClasses.length
                            ]
                          }
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center">
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Sweep {sweepNumber}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400">
            Click on a sweep box to select it and see the movement pattern. Then click "Select Instance" to assign the
            current instance.
          </div>
        </div>
      )}

      <Modal.Footer className="justify-between bg-gray-50 dark:bg-gray-800">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {currentInstance?.synced
            ? 'This instance has already been synced'
            : isBlindStudy
              ? selectedSweepBox !== null && selectedSweepBox !== undefined
                ? `Ready to assign to ${useIndexMode ? `Sweep ${selectedSweepBox + 1}` : sweepMovements[selectedSweepBox as keyof typeof sweepMovements]?.name}`
                : 'Select a sweep box first, then click "Select Instance" to assign'
              : 'Use arrow keys to navigate • Space to select/deselect • Esc to close'}
        </div>
        <div className="flex items-center gap-3">
          {currentInstance?.synced ? (
            <div className="px-6 py-2 text-sm font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed">
              Instance Already Synced
            </div>
          ) : (
            <button
              onClick={handleInstanceSelection}
              disabled={isBlindStudy && (selectedSweepBox === null || selectedSweepBox === undefined)}
              className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
                isBlindStudy && (selectedSweepBox === null || selectedSweepBox === undefined)
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : selectedInstances.has(currentInstance?.id)
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50'
                    : 'bg-blue-600 dark:bg-blue-700 text-white hover:bg-blue-700 dark:hover:bg-blue-800'
              }`}
            >
              {selectedInstances.has(currentInstance?.id)
                ? isBlindStudy
                  ? 'Remove Assignment'
                  : 'Deselect'
                : isBlindStudy
                  ? selectedSweepBox !== null && selectedSweepBox !== undefined
                    ? `Assign to ${useIndexMode ? `Sweep ${selectedSweepBox + 1}` : sweepMovements[selectedSweepBox as keyof typeof sweepMovements]?.name}`
                    : 'Select Sweep Box First'
                  : 'Select'}{' '}
              Instance
            </button>
          )}
        </div>
      </Modal.Footer>
      
      <style>{`
        /* Animation for the SVG path drawing itself */
        @keyframes draw-path {
          from { stroke-dashoffset: var(--path-length); }
          to { stroke-dashoffset: 0; }
        }

        /* Animations for the moving 'probe' marker */
        @keyframes sweep-1 {
          0% { top: 90%; left: 40%; transform: rotate(90deg) translate(-50%, -50%); opacity: 1; }
          25% { top: 65%; left: 40%; transform: rotate(90deg) translate(-50%, -50%); opacity: 1; }
          35% { top: 65%; left: 85%; transform: rotate(0deg) translate(-50%, -50%); opacity: 1; }
          100% { top: 65%; left: 15%; transform: rotate(0deg) translate(-50%, -50%); opacity: 1; }
        }
        .ultrasound-sweep-1 { 
          animation: sweep-1 3s ease-in-out infinite alternate; 
        }

        @keyframes sweep-2 { 0% { top: 35%; transform: rotate(90deg) } 100% { top: 85%; transform: rotate(90deg)} }
        .ultrasound-sweep-2 { left: 21%; transform: translateX(-50%); animation: sweep-2 2.5s ease-in-out infinite alternate; }

        @keyframes sweep-3 { 0% { top: 35%;  transform: rotate(90deg)} 100% { top: 85%;  transform: rotate(90deg)} }
        .ultrasound-sweep-3 { left: 46%; transform: translateX(-50%); animation: sweep-3 2.5s ease-in-out infinite alternate; }

        @keyframes sweep-4 { 0% { top: 35%; transform: rotate(90deg)} 100% { top: 85%; transform: rotate(90deg)} }
        .ultrasound-sweep-4 { left: 71%; transform: translateX(-50%); animation: sweep-4 2.5s ease-in-out infinite alternate; }

        @keyframes sweep-5 { 0% { left: 15%; } 100% { left: 85%; } }
        .ultrasound-sweep-5 { top: 50%; transform: translate(-50%, -50%); animation: sweep-5 3s ease-in-out infinite alternate; }

        @keyframes sweep-6 { 0% { left: 15%; } 100% { left: 85%; } }
        .ultrasound-sweep-6 { top: 75%; transform: translate(-50%, -50%); animation: sweep-6 3s ease-in-out infinite alternate; }

        @keyframes sweep-7 { 0% { top: 80%; left: 20%; } 50% { top: 90%; left: 50%; } 100% { top: 80%; left: 80%; } }
        .ultrasound-sweep-7 { animation: sweep-7 3.5s ease-in-out infinite alternate; transform: translate(-50%, -50%); }
      `}</style>
    </Modal>
  );
};